<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import {
  LucideSearch,
  LucideNewspaper,
  LucideCircleX,
  LucideChevronDown,
  LucideCheck
} from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { SearchResponse } from 'meilisearch'
import {
  EntityExchangeOfficeItem,
  EntityExchangeOfficeItemSkeleton
} from '~/components/entity/exchange-office'
import type { IExchangeOffice, IExchangeOfficeQueryParams } from '~/components/entity/exchange-office'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxTrigger
} from '~/components/ui/combobox'
import type { ILocation, IResponseData } from '~/types'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

const hitsPerPage = Number(route.query.per_page || 10)
const searchQueryString = ref(String(route.query.q || ''))
const currentPage = ref(Number(route.query.page) || 1)

const debouncedQuery = useDebounce(searchQueryString, 500)

// Location filter: the head branch city or region of an exchange office ("l" in the URL)
const ALL_LOCATIONS = 'all'
const selectedLocation = ref(/^\d+$/.test(String(route.query.l)) ? String(route.query.l) : ALL_LOCATIONS)

const { data: locationsData } = useApi<IResponseData<ILocation[]>>(
  () => useUrlWithParams('/locations', { per_page: 1000 }),
  { key: 'exchange-offices-locations' }
)
const locations = computed(() => locationsData.value?.data || [])

// Without a query the list shows the first locations of the site order (Bishkek, Osh and the
// regions) plus the selected one; a query searches all of them
const DEFAULT_VISIBLE_LOCATIONS = 9
const locationQuery = ref('')
const isLocationListOpen = ref(false)

const selectedLocationTitle = computed(
  () => locations.value.find((location) => String(location.id) === selectedLocation.value)?.title
)

const visibleLocations = computed(() => {
  const query = locationQuery.value.trim().toLowerCase()

  if (query) {
    return locations.value.filter((location) => location.title.toLowerCase().includes(query))
  }

  const defaults = locations.value.slice(0, DEFAULT_VISIBLE_LOCATIONS)
  const selected = locations.value.find((location) => String(location.id) === selectedLocation.value)

  return selected && !defaults.includes(selected) ? [...defaults, selected] : defaults
})

watch(isLocationListOpen, (isOpen) => {
  if (isOpen) locationQuery.value = ''
})

// Compute search key based on all filter dependencies
const searchKey = computed(() =>
  ['exchange-offices-search', debouncedQuery.value, selectedLocation.value, currentPage.value].join('-')
)

// Use useAsyncData for SSR support
const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IExchangeOffice> | null>(
  searchKey,
  async () => {
    const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_exchange_offices`)

    try {
      return (await search(debouncedQuery.value, {
        hitsPerPage,
        sort: ['weight:desc', 'highlight:desc', 'updated_at:desc'],
        page: currentPage.value,
        filter:
          selectedLocation.value === ALL_LOCATIONS
            ? undefined
            : [`location_ids = ${Number(selectedLocation.value)}`],
        attributesToHighlight: ['title'],
        attributesToSearchOn: [
          'title.ky',
          'title.ru',
          'description.ky',
          'description.ru',
          'license',
          'okpo'
        ]
      })) as unknown as SearchResponse<IExchangeOffice>
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: true,
    watch: false,
    default: () => null
  }
)

const hasError = computed(() => !!searchError.value)

const typedHits = computed(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    logo: hit.logo,
    head_branch_location: hit.head_branch_location,
    license: hit.license,
    okpo: hit.okpo,
    mobile_phone: hit.mobile_phone,
    highlight: hit.highlight
  }))
})

// Initialize component
const isInitializing = ref(true)

onMounted(() => {
  isInitializing.value = false
})

const props = withDefaults(
  defineProps<{
    queryParams?: IExchangeOfficeQueryParams
    hideSearch?: boolean
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    }),
    hideSearch: false
  }
)

watch(
  () => props.queryParams,
  () => {
    if (isInitializing.value) return
    currentPage.value = 1
    searchQueryString.value = props.queryParams?.q || ''
  },
  { deep: true }
)

watch(debouncedQuery, () => {
  if (isInitializing.value) return
  currentPage.value = 1
  router.replace({
    query: {
      ...route.query,
      q: debouncedQuery.value,
      page: undefined
    }
  })
})

watch(selectedLocation, () => {
  if (isInitializing.value) return
  currentPage.value = 1
  router.replace({
    query: {
      ...route.query,
      l: selectedLocation.value === ALL_LOCATIONS ? undefined : selectedLocation.value,
      page: undefined
    }
  })
})

const pageLink = (page: number) => {
  const params = new URLSearchParams({ q: searchQueryString.value, page: String(page) })
  if (selectedLocation.value !== ALL_LOCATIONS) params.set('l', selectedLocation.value)
  return `?${params.toString()}`
}

const changePage = (page: number) => {
  router.replace({
    query: {
      ...route.query,
      q: searchQueryString.value,
      page
    }
  })
  currentPage.value = page
}
</script>

<template>
  <div class="space-y-5">
    <div v-if="!hideSearch" class="flex flex-col gap-3 md:flex-row">
      <div class="relative flex-1">
        <Input
          v-model="searchQueryString"
          :placeholder="$t('common.search_placeholder')"
          class="h-[52px] pl-14 pr-14"
        />
        <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
        <Button
          v-if="searchQueryString"
          size="icon"
          variant="link"
          class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
          @click="searchQueryString = ''"
        >
          <LucideCircleX />
        </Button>
      </div>
      <Combobox
        v-model="selectedLocation"
        v-model:open="isLocationListOpen"
        ignore-filter
        class="flex-shrink-0 md:w-[252px]"
      >
        <ComboboxAnchor class="w-full">
          <ComboboxTrigger
            :aria-label="$t('widget.exchange_office.meili_search.select_location')"
            class="flex h-[52px] w-full items-center justify-between rounded-md border bg-white px-4 text-start text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
          >
            <span class="truncate">
              {{ selectedLocationTitle ?? $t('widget.exchange_office.meili_search.all_locations') }}
            </span>
            <LucideChevronDown class="size-4 shrink-0 opacity-50" />
          </ComboboxTrigger>
        </ComboboxAnchor>
        <ComboboxList class="w-[var(--reka-combobox-trigger-width)] p-1">
          <div class="relative border-b border-gray-100 pb-1">
            <LucideSearch class="absolute left-2.5 top-2.5 size-4 text-gray-500" />
            <ComboboxInput
              v-model="locationQuery"
              :display-value="() => ''"
              :placeholder="$t('widget.exchange_office.meili_search.search_location')"
              class="border-0 pl-8 shadow-none focus-visible:ring-0"
            />
          </div>
          <ComboboxEmpty>{{ $t('common.nothing_found') }}</ComboboxEmpty>
          <div class="max-h-72 overflow-y-auto pt-1">
            <ComboboxItem v-if="!locationQuery.trim()" :value="ALL_LOCATIONS">
              {{ $t('widget.exchange_office.meili_search.all_locations') }}
              <ComboboxItemIndicator>
                <LucideCheck class="size-4" />
              </ComboboxItemIndicator>
            </ComboboxItem>
            <ComboboxItem
              v-for="location in visibleLocations"
              :key="location.id"
              :value="String(location.id)"
            >
              {{ location.title }}
              <ComboboxItemIndicator>
                <LucideCheck class="size-4" />
              </ComboboxItemIndicator>
            </ComboboxItem>
          </div>
        </ComboboxList>
      </Combobox>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="exchange-offices">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="exchange-offices" tag="h2" class="sr-only">{{
          $t('common.exchange_offices')
        }}</Heading>
        <template v-for="item in typedHits" :key="item.id">
          <EntityExchangeOfficeItem :item="item" :hide-organization-details="true" />
        </template>

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev: currentPage > 1 ? pageLink(result.page ? result.page - 1 : 1) : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? pageLink(result.page ? result.page + 1 : 1)
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityExchangeOfficeItemSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>
  </div>
</template>
